import {
  isNotNull,
  objHasComponentProp,
  objHasComponentsProp,
  PrismicItemField,
} from '@grupoboticario/barril-del-chavo'
import { VideoShowcase } from 'src/@interfaces'
import * as yup from 'yup'

export const VIDEO_SHOWCASES_COMPONENT_NAME = 'VitrineMinhaLojaDigitalVitrineVideos'

export const videoShowcaseSchema = yup.object({
  component: yup.string().oneOf([VIDEO_SHOWCASES_COMPONENT_NAME]).required(),
  options: yup
    .object({
      titulo: yup.string().required(),
      sku: yup.string().required(),
      linkVideoYoutube: yup
        .object({
          linkType: yup.string().optional(),
          key: yup.string().optional(),
          url: yup.string().required(),
        })
        .required(),
      imagemCapa: yup
        .object({
          dimensions: yup.object().optional(),
          alt: yup.string().nullable(),
          copyright: yup.string().nullable(),
          url: yup.string().url().optional(),
          id: yup.string().optional(),
        })
        .optional(),
      action: yup.object().optional(),
    })
    .required(),
})

export type VideoShowcaseFromDynamicContent = yup.InferType<typeof videoShowcaseSchema>

class TransformToVideoShowcaseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TransformToVideoShowcaseError'
  }
}

export function isUrlFromYoutubeShorts(url: string): boolean {
  return url.includes('youtube.com/shorts')
}

export function buildYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/sddefault.jpg`
}

export function transformToVideoShowcase() {
  return (dynamicComponent: unknown): VideoShowcase | null => {
    try {
      const parsed = videoShowcaseSchema.validateSync(dynamicComponent, { stripUnknown: false })

      let thumbnail = parsed.options.imagemCapa?.url || ''

      if (!isUrlFromYoutubeShorts(parsed.options.linkVideoYoutube.url)) {
        throw new TransformToVideoShowcaseError('Invalid YouTube Shorts URL for video showcase')
      }

      if (!thumbnail) {
        const videoId = parsed.options.linkVideoYoutube.url.split('/').pop()

        if (!videoId) {
          throw new TransformToVideoShowcaseError('Video id not found in YouTube Shorts URL')
        }

        thumbnail = buildYouTubeThumbnailUrl(videoId)
      }

      return {
        name: parsed.options.titulo,
        sku: parsed.options.sku,
        url: parsed.options.linkVideoYoutube.url,
        thumbnail,
      }
    } catch (e) {
      console.error('Error transforming dynamic component to video showcase:', e)
      const noticeErrorAttributes = {
        customAttributes: {
          component: VIDEO_SHOWCASES_COMPONENT_NAME,
          error: (e as Error).message,
          originalValue: JSON.stringify(dynamicComponent),
        },
        customError: true,
      }
      if (e instanceof yup.ValidationError) {
        window.NREUM.noticeError(
          new TransformToVideoShowcaseError(
            'Failed to transform dynamic component to video showcase',
          ),
          noticeErrorAttributes,
        )
      }
      if (e instanceof TransformToVideoShowcaseError) {
        window.NREUM.noticeError(e, noticeErrorAttributes)
      }

      return null
    }
  }
}

export function transformToShowcases(
  dynamicComponents: PrismicItemField['components'],
): VideoShowcase[] {
  return dynamicComponents
    .filter((c) => objHasComponentProp(c) && c.component === VIDEO_SHOWCASES_COMPONENT_NAME)
    .map(transformToVideoShowcase())
    .filter(isNotNull)
}

export function getVideoShowcasesFromDynamicContent(
  dynamicContent: PrismicItemField,
): VideoShowcase[] {
  if (!objHasComponentsProp(dynamicContent)) {
    return []
  }

  return transformToShowcases(dynamicContent.components)
}
